import React, { useState, useEffect } from 'react';
import MainLayout from '../layout/MainLayout';
import Card, { CardHeader, CardTitle, CardBody } from '../core/Card/Card';
import KPICard from '../core/KPICard/KPICard';
import {
  FileText,
  Search,
  Download,
  Filter,
  User,
  Calendar,
  Activity,
  Shield,
  Trash2,
  Eye,
  ChevronLeft,
  ChevronRight,
  RefreshCw
} from 'lucide-react';
import apiService from '../../services/api';
import './admin-audit-logs.scss';

interface AuditLog {
  id: number;
  user_id: number;
  email?: string;
  first_name?: string;
  last_name?: string;
  action: string;
  table_name?: string;
  record_id?: string;
  old_values?: any;
  new_values?: any;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
}

interface AuditStats {
  total_logs: number;
  logs_today: number;
  logs_this_week: number;
  logs_this_month: number;
  most_active_users: Array<{
    user_id: number;
    email: string;
    first_name: string;
    last_name: string;
    action_count: number;
  }>;
  most_common_actions: Array<{
    action: string;
    count: number;
  }>;
  activity_timeline: Array<{
    date: string;
    count: number;
  }>;
}

const AdminAuditLogs: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [stats, setStats] = useState<AuditStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    search: '',
    action: '',
    table_name: '',
    start_date: '',
    end_date: '',
    user_id: ''
  });

  const [availableActions, setAvailableActions] = useState<string[]>([]);
  const [availableTables, setAvailableTables] = useState<string[]>([]);

  const itemsPerPage = 20;

  useEffect(() => {
    loadAuditLogs();
    loadStats();
    loadFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filters]);

  const loadAuditLogs = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/audit', {
        params: {
          ...filters,
          limit: itemsPerPage,
          offset: (currentPage - 1) * itemsPerPage
        }
      });

      if (response.data.status === 'success') {
        setLogs(response.data.data);
        setTotalItems(response.data.pagination.total);
      }
    } catch (error) {
      console.error('Error loading audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await apiService.get('/audit/stats');
      if (response.data.status === 'success') {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error loading audit stats:', error);
    }
  };

  const loadFilterOptions = async () => {
    try {
      const [actionsResponse, tablesResponse] = await Promise.all([
        apiService.get('/audit/actions'),
        apiService.get('/audit/tables')
      ]);

      if (actionsResponse.data.status === 'success') {
        setAvailableActions(actionsResponse.data.data);
      }
      if (tablesResponse.data.status === 'success') {
        setAvailableTables(tablesResponse.data.data);
      }
    } catch (error) {
      console.error('Error loading filter options:', error);
    }
  };

  const handleViewDetails = async (log: AuditLog) => {
    try {
      const response = await apiService.get(`/audit/${log.id}`);
      if (response.data.status === 'success') {
        setSelectedLog(response.data.data);
        setShowDetails(true);
      }
    } catch (error) {
      console.error('Error loading log details:', error);
    }
  };

  const handleExport = async () => {
    try {
      const response = await apiService.get('/audit/export', {
        params: filters
      });

      if (response.data.status === 'success') {
        const data = response.data.data;
        const csvContent = [
          ['ID', 'Usuario', 'Nombre', 'Acción', 'Tabla', 'ID Registro', 'IP', 'Navegador', 'Fecha'].join(','),
          ...data.map((row: any) => [
            row.id,
            row.user,
            row.name,
            row.action,
            row.table,
            row.record_id,
            row.ip_address,
            row.user_agent,
            row.created_at
          ].join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error exporting audit logs:', error);
    }
  };

  const handleCleanup = async () => {
    if (!window.confirm('¿Está seguro de eliminar los registros de auditoría antiguos (más de 90 días)? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      const response = await apiService.delete('/audit/cleanup', {
        params: { days: 90 }
      });

      if (response.data.status === 'success') {
        alert(response.data.message);
        loadAuditLogs();
        loadStats();
      }
    } catch (error) {
      console.error('Error cleaning up audit logs:', error);
      alert('Error al limpiar registros antiguos');
    }
  };

  const getActionBadgeVariant = (action: string): 'default' | 'success' | 'warning' | 'danger' | 'info' => {
    const actionMap: Record<string, 'default' | 'success' | 'warning' | 'danger' | 'info'> = {
      'CREATE': 'success',
      'UPDATE': 'info',
      'DELETE': 'danger',
      'LOGIN': 'info',
      'LOGOUT': 'default',
      'APPROVE': 'success',
      'REJECT': 'warning',
      'COMPLETE': 'success'
    };
    return actionMap[action] || 'default';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  return (
    <MainLayout>
      <div className="admin-dashboard">
        <div className="page-header">
          <div className="page-header__info">
            <div className="eyebrow">{(() => { const d = new Date(); return `${d.toLocaleDateString('es', { weekday: 'long' })} ${d.getDate()} de ${d.toLocaleDateString('es', { month: 'short' }).replace('.', '')} · Administrador`.toUpperCase(); })()}</div>
            <h1 className="heading-1">Registros de <em>auditoría</em></h1>
            <p className="page-meta">
              {totalItems.toLocaleString()} registros · Sistema de trazabilidad
            </p>
          </div>
          <div className="page-header__actions">
            <button className="btn btn--outline" onClick={() => loadAuditLogs()}>
              <RefreshCw size={16} /> Actualizar
            </button>
            <button className="btn btn--outline" onClick={handleExport}>
              <Download size={16} /> Exportar CSV
            </button>
            <button className="btn btn--danger" onClick={handleCleanup}>
              <Trash2 size={16} /> Limpiar Antiguos
            </button>
          </div>
        </div>

        {stats && (
          <div className="kpi-grid">
            <KPICard
              label="Total Registros"
              value={stats.total_logs.toLocaleString()}
              icon={<FileText size={24} />}
              iconColor="navy"
            />
            <KPICard
              label="Hoy"
              value={stats.logs_today.toLocaleString()}
              icon={<Activity size={24} />}
              iconColor="green"
            />
            <KPICard
              label="Esta Semana"
              value={stats.logs_this_week.toLocaleString()}
              icon={<Calendar size={24} />}
              iconColor="gold"
            />
            <KPICard
              label="Este Mes"
              value={stats.logs_this_month.toLocaleString()}
              icon={<User size={24} />}
              iconColor="amber"
            />
          </div>
        )}

        <Card>
          <CardHeader
            action={
              <button
                className="btn btn--outline btn--sm"
                onClick={() => setFilters({
                  search: '',
                  action: '',
                  table_name: '',
                  start_date: '',
                  end_date: '',
                  user_id: ''
                })}
              >
                <RefreshCw size={16} />
                Limpiar filtros
              </button>
            }
          >
            <CardTitle>
              <Filter size={20} />
              Filtros de Búsqueda
            </CardTitle>
          </CardHeader>
          <CardBody>
            <div className="audit-filters-grid">
              {/* Search Input */}
              <div className="filter-field filter-field--search">
                <label className="filter-label">
                  <Search size={16} />
                  Buscar
                </label>
                <div className="input-wrapper">
                  <input
                    type="text"
                    className="filter-input"
                    placeholder="Buscar en registros..."
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  />
                </div>
              </div>

              {/* Action Select */}
              <div className="filter-field">
                <label className="filter-label">
                  <Activity size={16} />
                  Acción
                </label>
                <div className="select-wrapper">
                  <select
                    className="filter-select"
                    value={filters.action}
                    onChange={(e) => setFilters({ ...filters, action: e.target.value })}
                  >
                    <option value="">Todas las acciones</option>
                    {availableActions.map(action => (
                      <option key={action} value={action}>{action}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Table Select */}
              <div className="filter-field">
                <label className="filter-label">
                  <FileText size={16} />
                  Tabla
                </label>
                <div className="select-wrapper">
                  <select
                    className="filter-select"
                    value={filters.table_name}
                    onChange={(e) => setFilters({ ...filters, table_name: e.target.value })}
                  >
                    <option value="">Todas las tablas</option>
                    {availableTables.map(table => (
                      <option key={table} value={table}>{table}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Date Range */}
              <div className="filter-field">
                <label className="filter-label">
                  <Calendar size={16} />
                  Fecha Inicio
                </label>
                <div className="input-wrapper">
                  <input
                    type="date"
                    className="filter-input"
                    value={filters.start_date}
                    onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                  />
                </div>
              </div>

              <div className="filter-field">
                <label className="filter-label">
                  <Calendar size={16} />
                  Fecha Fin
                </label>
                <div className="input-wrapper">
                  <input
                    type="date"
                    className="filter-input"
                    value={filters.end_date}
                    onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                  />
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        <Card padding="none">
          <CardHeader>
            <CardTitle>
              <FileText size={20} />
              Registros de Auditoría ({totalItems.toLocaleString()})
            </CardTitle>
          </CardHeader>
          <CardBody>
            {loading ? (
              <div className="loading-state">
                <RefreshCw className="spinning" size={40} />
                <p>Cargando registros...</p>
              </div>
            ) : (
              <>
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Fecha</th>
                        <th>Usuario</th>
                        <th>Acción</th>
                        <th>Tabla</th>
                        <th>Registro</th>
                        <th>IP</th>
                        <th className="actions-column">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map(log => (
                        <tr key={log.id}>
                          <td>{log.id}</td>
                          <td>{formatDate(log.created_at)}</td>
                          <td>
                            <div>
                              <div style={{ fontWeight: 500 }}>
                                {log.first_name} {log.last_name}
                              </div>
                              <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                {log.email || 'Sistema'}
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className={`badge badge--${getActionBadgeVariant(log.action)}`}>
                              {log.action}
                            </span>
                          </td>
                          <td>{log.table_name || '-'}</td>
                          <td>{log.record_id || '-'}</td>
                          <td>{log.ip_address || '-'}</td>
                          <td className="actions-column">
                            <button
                              className="btn btn--icon btn--sm"
                              onClick={() => handleViewDetails(log)}
                              title="Ver detalles"
                            >
                              <Eye size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {totalPages > 1 && (
                  <div className="pagination">
                    <button
                      className="btn btn--secondary"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft size={20} />
                      Anterior
                    </button>
                    <span className="pagination-info">
                      Página {currentPage} de {totalPages}
                    </span>
                    <button
                      className="btn btn--secondary"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Siguiente
                      <ChevronRight size={20} />
                    </button>
                  </div>
                )}
              </>
            )}
          </CardBody>
        </Card>

        {showDetails && selectedLog && (
          <div className="modal-overlay" onClick={() => setShowDetails(false)}>
            <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Detalles del Registro #{selectedLog.id}</h3>
                <button className="modal-close" onClick={() => setShowDetails(false)}>×</button>
              </div>
              <div className="modal-body">
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Usuario:</label>
                    <span>{selectedLog.first_name} {selectedLog.last_name} ({selectedLog.email})</span>
                  </div>
                  <div className="detail-item">
                    <label>Acción:</label>
                    <span className={`badge badge--${getActionBadgeVariant(selectedLog.action)}`}>
                      {selectedLog.action}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Fecha:</label>
                    <span>{formatDate(selectedLog.created_at)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Tabla:</label>
                    <span>{selectedLog.table_name || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>ID Registro:</label>
                    <span>{selectedLog.record_id || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Dirección IP:</label>
                    <span>{selectedLog.ip_address || '-'}</span>
                  </div>
                </div>

                {selectedLog.old_values && (
                  <div className="values-section">
                    <h4>Valores Antiguos</h4>
                    <pre className="json-display">
                      {JSON.stringify(selectedLog.old_values, null, 2)}
                    </pre>
                  </div>
                )}

                {selectedLog.new_values && (
                  <div className="values-section">
                    <h4>Valores Nuevos</h4>
                    <pre className="json-display">
                      {JSON.stringify(selectedLog.new_values, null, 2)}
                    </pre>
                  </div>
                )}

                {selectedLog.user_agent && (
                  <div className="values-section">
                    <h4>Navegador</h4>
                    <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>{selectedLog.user_agent}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default AdminAuditLogs;
